import { useEffect, useState } from "react";
import { X, ImagePlus, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * MediaPicker — modal for choosing a Media Library file to bind into a CMS
 * text field. Also supports drag-drop / click upload of new files without
 * leaving the picker.
 *
 * Props:
 *   open      — boolean
 *   onClose   — () => void
 *   onPick    — (url: string) => void          // full API-path URL, e.g. /api/uploads/abc.jpg
 *   currentValue?: string                       // pre-selected URL
 */
export default function MediaPicker({ open, onClose, onPick, currentValue }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media");
      setItems(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load media");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (err) {
          toast.error(`${f.name}: ${err?.response?.data?.detail || "Upload failed"}`);
        }
      }
      toast.success("Upload complete");
      await load();
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onDelete = async (filename) => {
    if (!window.confirm(`Delete ${filename}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/media/${filename}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  if (!open) return null;

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? items.filter((it) => it.filename.toLowerCase().includes(needle))
    : items;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="media-picker">
      <div className="w-full max-w-4xl my-8 bg-[var(--ed-bg)] border border-[var(--ed-rule)] rounded-sm shadow-2xl">
        <header className="px-6 py-4 border-b border-[var(--ed-rule)] flex items-center justify-between gap-4">
          <div>
            <p className="ed-overline mb-1">Media Library</p>
            <h2 className="font-serif text-xl text-[var(--ed-ink)]">Pick a photograph</h2>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2 bg-[var(--ed-emerald)] text-white hover:bg-[var(--ed-emerald-deep)] rounded-sm flex items-center gap-1.5 cursor-pointer">
              <Upload size={11} /> {uploading ? "Uploading…" : "Upload"}
              <input
                data-testid="media-picker-upload"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                multiple onChange={onUpload} className="hidden"
              />
            </label>
            <button onClick={onClose} className="text-[var(--ed-ink-muted)] hover:text-[var(--ed-ink)] p-1"><X size={20} /></button>
          </div>
        </header>

        <div className="px-6 py-3 border-b border-[var(--ed-rule)] bg-[var(--ed-paper)]/40">
          <input
            data-testid="media-picker-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by filename…"
            className="w-full px-3 py-2 text-sm bg-[var(--ed-bg)] border border-[var(--ed-rule)] focus:border-[var(--ed-emerald)] text-[var(--ed-ink)] outline-none rounded-sm"
          />
        </div>

        <div className="p-6 max-h-[65vh] overflow-y-auto">
          {loading ? (
            <p className="text-[var(--ed-ink-muted)] py-16 text-center">Loading…</p>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-[var(--ed-rule)] p-14 text-center rounded-sm">
              <ImagePlus size={22} className="mx-auto text-[var(--ed-ink-muted)] mb-3" />
              <p className="text-[var(--ed-ink-muted)] text-sm">
                {items.length === 0 ? "No files yet — upload your first photograph." : "No files match this search."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {filtered.map((it) => {
                const selected = it.url === currentValue;
                const key = it.id || it.public_id || it.url;
                const onSelect = () => { onPick(it.url); onClose(); };
                return (
                  <div
                    key={key}
                    data-testid={`media-pick-${it.filename}`}
                    onClick={onSelect}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
                    role="button"
                    tabIndex={0}
                    className={`group text-left border rounded-sm overflow-hidden bg-[var(--ed-bg)] transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--ed-emerald)]/60 ${
                      selected
                        ? "border-[var(--ed-emerald)] ring-2 ring-[var(--ed-emerald)]/40"
                        : "border-[var(--ed-rule)] hover:border-[var(--ed-ink)]"
                    }`}
                    title={it.filename}
                  >
                    <div className="aspect-square overflow-hidden bg-[var(--ed-paper)] relative">
                      <img src={it.url} alt={it.filename} className="w-full h-full object-cover" loading="lazy" />
                      {selected && (
                        <span className="absolute top-2 left-2 bg-[var(--ed-emerald)] text-white text-[0.55rem] tracking-widest uppercase px-2 py-0.5 rounded-sm">
                          Current
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onDelete(it.filename); }}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-red-600/90 text-white p-1.5 rounded-sm hover:bg-red-700"
                        aria-label="Delete"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                    <p className="px-2 py-1.5 text-[0.65rem] text-[var(--ed-ink-muted)] truncate">{it.filename}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {currentValue && (
          <div className="px-6 py-4 border-t border-[var(--ed-rule)] bg-[var(--ed-paper)]/40 flex items-center justify-between gap-3">
            <p className="text-[var(--ed-ink-muted)] text-xs truncate">Currently: <span className="text-[var(--ed-ink)]">{currentValue}</span></p>
            <button
              type="button"
              data-testid="media-pick-clear"
              onClick={() => { onPick(""); onClose(); }}
              className="text-[0.6rem] uppercase tracking-[0.22em] border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] px-3 py-2 rounded-sm"
            >
              Clear image
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
